import { useState } from 'react';

import { Controller, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';

import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';

import { COUNTRIES } from '@/config';
import { useRegisterMutation } from '@/features/auth/store/api';
import { PrimaryButton, Checkbox, Combobox, Input, FormField } from '@/lib/ui';
import { cn, extractServerError, notify } from '@/lib/utils';
import { ROUTES } from '@/routes';

import { RegisterFormSchema } from '../schemas';
import type { RegisterApiPayload, RegisterFormValues } from '../types';
import { AuthScreenShell } from './AuthScreenShell';

// ── Helpers ───────────────────────────────────────────────────────────────────

const authFieldInputClass =
  'border-white/10 bg-black/40 text-white placeholder:text-white/45 ring-offset-background';

// ── Component ─────────────────────────────────────────────────────────────────

export function RegisterForm() {
  const navigate = useNavigate();
  const [register, { isLoading }] = useRegisterMutation();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const {
    register: formRegister,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<RegisterFormValues>({
    resolver: zodResolver(RegisterFormSchema),
    defaultValues: { termsAccepted: false },
  });

  const onSubmit = async (data: RegisterFormValues) => {
    const userPayload: RegisterApiPayload['user'] = {
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
      phone: data.phone,
      country: data.country,
      password: data.password,
      ...(data.invitation_code ? { invitation_code: data.invitation_code } : {}),
    };
    try {
      await register({ user: userPayload, is_social: false }).unwrap();
      // Org setup happens after the first sign-in, so every signup ends at login.
      notify.success('Account created! Please verify your email before signing in.');
      navigate(ROUTES.login, { replace: true });
    } catch (err) {
      notify.error(extractServerError(err, 'Registration failed. Please try again.'));
    }
  };

  return (
    <AuthScreenShell activeTab="register" maxWidthClass="max-w-lg">
      <p className="text-sm text-white/60">Join Moveris to start building with our APIs.</p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="First name"
            htmlFor="register-first-name"
            error={errors.first_name?.message}
            labelClassName="!text-white !font-semibold"
          >
            <Input
              id="register-first-name"
              placeholder="Jane"
              autoComplete="given-name"
              className={authFieldInputClass}
              {...formRegister('first_name')}
            />
          </FormField>
          <FormField
            label="Last name"
            htmlFor="register-last-name"
            error={errors.last_name?.message}
            labelClassName="!text-white !font-semibold"
          >
            <Input
              id="register-last-name"
              placeholder="Doe"
              autoComplete="family-name"
              className={authFieldInputClass}
              {...formRegister('last_name')}
            />
          </FormField>
        </div>

        <FormField
          label="Email"
          htmlFor="register-email"
          error={errors.email?.message}
          labelClassName="!text-white !font-semibold"
        >
          <Input
            id="register-email"
            type="email"
            placeholder="Enter your email"
            autoComplete="email"
            className={authFieldInputClass}
            {...formRegister('email')}
          />
        </FormField>

        <FormField
          label="Phone"
          htmlFor="register-phone"
          error={errors.phone?.message}
          labelClassName="!text-white !font-semibold"
        >
          <Input
            id="register-phone"
            type="tel"
            placeholder="+1 555 000 0000"
            autoComplete="tel"
            className={authFieldInputClass}
            {...formRegister('phone')}
          />
        </FormField>

        <FormField
          label="Country"
          htmlFor="register-country"
          error={errors.country?.message}
          labelClassName="!text-white !font-semibold"
        >
          <Controller
            control={control}
            name="country"
            render={({ field }) => (
              <Combobox
                options={COUNTRIES.map((c) => ({ value: c.code, label: c.name }))}
                value={field.value}
                onChange={field.onChange}
                placeholder="Select your country"
                searchPlaceholder="Search country..."
                className={authFieldInputClass}
              />
            )}
          />
        </FormField>

        <FormField
          label="Password"
          htmlFor="register-password"
          error={errors.password?.message}
          labelClassName="!text-white !font-semibold"
        >
          <div className="relative">
            <Input
              id="register-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              autoComplete="new-password"
              className={cn('pr-10', authFieldInputClass)}
              {...formRegister('password')}
            />
            <button
              type="button"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((p) => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
            >
              {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </FormField>

        <FormField
          label="Confirm password"
          htmlFor="register-confirm-password"
          error={errors.confirmPassword?.message}
          labelClassName="!text-white !font-semibold"
        >
          <div className="relative">
            <Input
              id="register-confirm-password"
              type={showConfirm ? 'text' : 'password'}
              placeholder="Enter your password"
              autoComplete="new-password"
              className={cn('pr-10', authFieldInputClass)}
              {...formRegister('confirmPassword')}
            />
            <button
              type="button"
              aria-label={showConfirm ? 'Hide confirm password' : 'Show confirm password'}
              onClick={() => setShowConfirm((p) => !p)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
            >
              {showConfirm ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
            </button>
          </div>
        </FormField>

        <FormField
          label="Invitation code (optional)"
          htmlFor="register-invitation"
          error={errors.invitation_code?.message}
          labelClassName="!text-white !font-semibold"
        >
          <Input
            id="register-invitation"
            placeholder="ABC123"
            maxLength={6}
            className={authFieldInputClass}
            {...formRegister('invitation_code')}
          />
        </FormField>

        <FormField error={errors.termsAccepted?.message}>
          <Controller
            control={control}
            name="termsAccepted"
            render={({ field }) => (
              <label className="flex cursor-pointer items-start gap-3">
                <Checkbox
                  className="mt-0.5"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <span className="text-sm text-white/70">
                  I agree to the{' '}
                  <a
                    href="https://moveris.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white underline hover:no-underline"
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://moveris.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white underline hover:no-underline"
                  >
                    Privacy Policy
                  </a>
                </span>
              </label>
            )}
          />
        </FormField>

        <PrimaryButton type="submit" className="w-full font-semibold" disabled={isLoading}>
          {isLoading ? 'Creating account…' : 'Create account'}
        </PrimaryButton>
      </form>

      <p className="text-center text-sm text-white/60">
        Already have an account?{' '}
        <Link to={ROUTES.login} className="font-semibold text-white hover:underline">
          Sign in
        </Link>
      </p>
    </AuthScreenShell>
  );
}
